import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer } from '@/UI/styles';
import { ThreeDRotateSlashIcon } from '@/UI/Icons/ThreeDRotateSlashIcon';
import { ThreeDRotateIcon } from '@/UI/Icons/ThreeDRotateIcon';
import { isMediaAsset } from '@/components/Common/ImageList/utils';
import type { UploadedAsset, MediaAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

const formatMediaMetadata = (file: MediaAsset) => {
  if (!file) return '';

  const parts = [
    `${file.width}x${file.height}`,
    file.duration ? `${file.duration.toFixed(2)}s` : undefined,
    file.fps ? `${file.fps.toFixed(2)} fps` : undefined,
  ].filter(Boolean);

  return parts.join(' | ');
};

const renderFileMetadata = (file: MediaAsset) => {
  if (!file) return null;

  if (!['image', 'video'].includes(file.type)) return null;
  return (
    <Typography variant="label-sm-rg" sx={{ lineHeight: 1 }}>
      {formatMediaMetadata(file)}
    </Typography>
  );
};

export interface AssetMetadataToolbarProps {
  bottomOffset?: number;
  isSingleAssetMode: boolean;
  setIs3DLocked?: (is3DLocked: boolean) => void;
  is3DLocked?: boolean;
  asset?: UploadedAsset | FileKind;
}

export const AssetMetadataToolbar = ({
  bottomOffset = 12,
  isSingleAssetMode,
  setIs3DLocked,
  is3DLocked,
  asset,
}: AssetMetadataToolbarProps) => {
  const isMedia = isMediaAsset(asset);
  const is3D = isMedia && asset?.type === '3D';
  return (
    <>
      {isSingleAssetMode && is3D && !!setIs3DLocked ? (
        <Lock3DOverlay is3DLocked={is3DLocked ?? false} setIs3DLocked={setIs3DLocked} bottomOffset={bottomOffset} />
      ) : isSingleAssetMode && isMedia ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: bottomOffset,
            left: 16,
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          <FlexCenVer sx={{ pointerEvents: 'all' }}>
            <FlexCenVer>{renderFileMetadata(asset as MediaAsset)}</FlexCenVer>
          </FlexCenVer>
        </Box>
      ) : null}
    </>
  );
};

export const Lock3DOverlay = ({
  is3DLocked,
  setIs3DLocked,
  bottomOffset = 12,
}: {
  is3DLocked: boolean;
  setIs3DLocked: (is3DLocked: boolean) => void;
  bottomOffset?: number;
}) => {
  const toggle3DLock = useCallback(() => {
    setIs3DLocked?.(!is3DLocked);
  }, [setIs3DLocked, is3DLocked]);

  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: bottomOffset,
        left: 16,
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      <FlexCenVer sx={{ pointerEvents: 'all' }}>
        <Tooltip
          title={
            is3DLocked
              ? t(I18N_KEYS.NODE_IMAGE_LIST.CAMERA_CONTROLS.UNLOCK)
              : t(I18N_KEYS.NODE_IMAGE_LIST.CAMERA_CONTROLS.LOCK)
          }
          disableInteractive
        >
          <AppIconButton
            onClick={(e) => {
              e.stopPropagation();
              toggle3DLock();
            }}
            width={32}
            height={32}
            mode="on-light"
          >
            {is3DLocked ? <ThreeDRotateSlashIcon /> : <ThreeDRotateIcon />}
          </AppIconButton>
        </Tooltip>
      </FlexCenVer>
    </Box>
  );
};
